import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type {
  User,
  AuthContextType,
  ExtendedUser,
  TenantOverrideInfo,
} from "./types";
import type { AuthClient } from "./authClient";
import type { TokenManager } from "./tokenManager";
import { getOverrideTenantId } from "../utils/tenantOverride";
import { setSessionExpiredHandler } from "./sessionExpiry";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export interface AuthProviderProps {
  children: ReactNode;
  /** App's auth client (differs per app: mapUser, JWT fallback, register/captcha). Same auth server. */
  authClient: AuthClient;
  tokenManager: TokenManager;
  /**
   * Clears tenant-scoped cache/storage when switching between users. Only used by apps that
   * enable user-switching (backoffice); apps that never call `switchToUser` can omit it.
   */
  resetTenantScopedClientState?: () => Promise<void>;
}

function mapOverrideState(): TenantOverrideInfo | null {
  const overrideTenantId = getOverrideTenantId();
  if (!overrideTenantId) {
    return null;
  }

  const overrideUserRaw = localStorage.getItem("overrideUser");
  if (!overrideUserRaw || overrideUserRaw === "null") {
    return null;
  }

  try {
    const overrideUser = JSON.parse(overrideUserRaw) as User;
    return {
      targetUserId: overrideUser.id,
      targetUserName: overrideUser.userName,
      targetUserEmail: overrideUser.email,
      targetTenantId: overrideTenantId,
      expiresAt: 0,
    };
  } catch {
    return null;
  }
}

/**
 * Single auth provider shared by both apps. App-specific pieces (auth client, token manager,
 * tenant-cache reset) are injected as props; the same OpenIddict auth server backs both apps.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  authClient,
  tokenManager,
  resetTenantScopedClientState,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [tenantOverride, setTenantOverride] =
    useState<TenantOverrideInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const clearSession = useCallback(() => {
    localStorage.setItem("overrideTenantId", "null");
    localStorage.setItem("overrideUser", "null");
    localStorage.setItem("oldUser", "null");
    setUser(null);
    setToken(null);
    setTenantOverride(null);
    authClient.logout();
  }, [authClient]);

  useEffect(() => {
    setSessionExpiredHandler(clearSession);
    return () => setSessionExpiredHandler(null);
  }, [clearSession]);

  useEffect(() => {
    const checkAuth = async () => {
      // Everything below is wrapped so a rejection from getAccessToken() (or any other
      // unexpected throw) can never leave `loading` stuck at `true` forever.
      try {
        const storedToken = await tokenManager.getAccessToken();

        if (storedToken) {
          const overrideUser = localStorage.getItem("overrideUser");
          if (overrideUser && overrideUser !== "null") {
            try {
              const userData: User = JSON.parse(overrideUser);
              await authClient.verifyToken();
              setUser(userData);
              setToken(storedToken);
              setTenantOverride(mapOverrideState());
              return;
            } catch (error) {
              console.error("Override user token verification failed:", error);
            }
          }

          try {
            const userData = await authClient.verifyToken();
            setUser(userData);
            setToken(storedToken);
            setTenantOverride(mapOverrideState());
          } catch (error) {
            console.error("Token verification failed:", error);
            try {
              const newToken = await tokenManager.refreshToken();
              const userData = await authClient.verifyToken();
              setUser(userData);
              setToken(newToken);
              setTenantOverride(mapOverrideState());
            } catch (refreshError) {
              console.error("Token refresh failed:", refreshError);
              clearSession();
            }
          }
        }
      } catch (error) {
        console.error("[AuthContext] checkAuth failed unexpectedly:", error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [authClient, tokenManager, clearSession]);

  const login = async (email: string, password: string) => {
    try {
      const response = await authClient.login(email, password);
      setUser(response.user);
      setToken(response.token);
      setTenantOverride(null);
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  const logout = () => {
    clearSession();
  };

  const refreshUser = async () => {
    const storedToken = await tokenManager.getAccessToken();
    if (storedToken) {
      try {
        const userData = await authClient.verifyToken();
        setUser(userData);
        setToken(storedToken);
        setTenantOverride(mapOverrideState());
      } catch (error) {
        console.error("Failed to refresh user data:", error);
        clearSession();
      }
    } else {
      clearSession();
    }
  };

  const setUserAndToken = (userData: User, tokenValue: string) => {
    setUser(userData);
    setToken(tokenValue);
    setTenantOverride(mapOverrideState());
  };

  const switchToUser = async (extendedUser: ExtendedUser) => {
    const previousUser = user;
    const previousTenantOverride = tenantOverride;
    const previousOverrideTenantId = localStorage.getItem("overrideTenantId");
    const previousOverrideUser = localStorage.getItem("overrideUser");
    const previousOldUser = localStorage.getItem("oldUser");

    setLoading(true);
    try {
      const oldUser = localStorage.getItem("oldUser");
      if (!oldUser || oldUser === "null") {
        localStorage.setItem("oldUser", JSON.stringify(user));
      }

      localStorage.setItem("overrideTenantId", extendedUser.tenantId || "null");

      const switchedUser: User = {
        id: extendedUser.sub,
        userName: extendedUser.userName,
        email: extendedUser.email,
        firstName: extendedUser.firstName,
        lastName: extendedUser.lastName,
        role: extendedUser.role,
        tenantId: extendedUser.tenantId || undefined,
        photoURL: extendedUser.imagePath || undefined,
      };

      localStorage.setItem("overrideUser", JSON.stringify(switchedUser));
      await resetTenantScopedClientState?.();
      setUser(switchedUser);
      setTenantOverride(mapOverrideState());
    } catch (error) {
      if (previousOverrideTenantId === null) {
        localStorage.removeItem("overrideTenantId");
      } else {
        localStorage.setItem("overrideTenantId", previousOverrideTenantId);
      }
      if (previousOverrideUser === null) {
        localStorage.removeItem("overrideUser");
      } else {
        localStorage.setItem("overrideUser", previousOverrideUser);
      }
      if (previousOldUser === null) {
        localStorage.removeItem("oldUser");
      } else {
        localStorage.setItem("oldUser", previousOldUser);
      }
      setUser(previousUser);
      setTenantOverride(previousTenantOverride);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const clearUserSwitch = async () => {
    const previousUser = user;
    const previousTenantOverride = tenantOverride;
    const previousOverrideTenantId = localStorage.getItem("overrideTenantId");
    const previousOverrideUser = localStorage.getItem("overrideUser");
    const previousOldUser = localStorage.getItem("oldUser");

    setLoading(true);
    try {
      localStorage.setItem("overrideTenantId", "null");
      localStorage.setItem("overrideUser", "null");

      const oldUserJson = localStorage.getItem("oldUser");
      let restoredUser: User | null = null;
      if (oldUserJson && oldUserJson !== "null") {
        try {
          restoredUser = JSON.parse(oldUserJson) as User;
          localStorage.setItem("oldUser", "null");
        } catch (error) {
          console.error("Error parsing old user:", error);
        }
      }

      await resetTenantScopedClientState?.();
      if (restoredUser) {
        setUser(restoredUser);
      }
      setTenantOverride(null);
    } catch (error) {
      if (previousOverrideTenantId === null) {
        localStorage.removeItem("overrideTenantId");
      } else {
        localStorage.setItem("overrideTenantId", previousOverrideTenantId);
      }
      if (previousOverrideUser === null) {
        localStorage.removeItem("overrideUser");
      } else {
        localStorage.setItem("overrideUser", previousOverrideUser);
      }
      if (previousOldUser === null) {
        localStorage.removeItem("oldUser");
      } else {
        localStorage.setItem("oldUser", previousOldUser);
      }
      setUser(previousUser);
      setTenantOverride(previousTenantOverride);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    isAuthenticated: !!user && (!!token || tokenManager.hasRefreshToken()),
    loading,
    token,
    tenantOverride,
    refreshUser,
    setUserAndToken,
    switchToUser,
    clearUserSwitch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
